import { CustomObject } from './CustomObject';
import { ImageObject } from '../../image/ImageObject';
import { Grid } from '../Grid';
import { TextureView } from '~/engine/view/TextureView';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import {
    ContourRenderer,
    ContourRenderOptions,
} from '~/engine/core/render/renderers/ContourRenderer';
import { ContourTexture } from '../../structure/ContourTexture';
import { ReadonlyVec3 } from 'gl-matrix';

/** Object used as a render target by a Tool, wrapping a single ContourTexture. */
export class CustomTargetObject extends CustomObject {
    protected _grid: Grid;

    protected readonly _targetImage: ImageObject;

    protected readonly _imageGrid: Grid;

    protected _contourTexture?: ContourTexture;

    protected _color: ReadonlyVec3 = [1.0, 0.0, 0.0];

    /** Contour line thickness forwarded to ContourRenderer. */
    protected _contourThickness = 1.5;

    public constructor(
        image: ImageObject,
        texture?: ContourTexture,
        color?: ReadonlyVec3
    ) {
        super();
        this._targetImage = image;
        this._imageGrid = image.parent.grid;
        this._grid = new Grid(
            0,
            0,
            0,
            this._imageGrid.resX,
            this._imageGrid.resY,
            this._imageGrid.resZ,
            null
        );
        this._contourTexture = texture;
        if (color) {
            this._color = color;
        }
    }

    /** Rendering options forwarded to ContourRenderer. */
    public renderOptions?: ContourRenderOptions;

    /**
     * Whether publishing this object as the operation preview recedes the
     * REGULAR structures behind it (see `ContourLayer.draw`).
     *
     * The recede is not an opacity change: the contour shader folds the
     * alpha into RGB (`colorMask.rgb * colorMask.a`) so the layer FBO
     * stays opaque, which means every other structure is RE-TINTED — a
     * skin-tone EXTERNAL renders dark brown — for as long as a preview is
     * on screen. That is the right trade only when the preview is drawn
     * OVER the structures it replaces and would otherwise be lost among
     * them (margin / ring / boolean, and the Body threshold preset, whose
     * preview sits exactly on the existing EXTERNAL in the same color).
     * A preview that lands somewhere else entirely must leave the
     * surrounding structures in their own clinical colors: structures are
     * identified by color, and silently re-tinting them is a display
     * defect, not an affordance.
     *
     * Default true — the historical behavior of every existing preview.
     */
    public dimsOtherStructures = true;

    /**
     * Whether publishing this object as the operation preview HIDES the
     * SELECTED structure — the one this preview was computed from — for as
     * long as the preview is on screen (see `ContourLayer.draw`
     * and {@link ContourRenderOptions.hideSelected}).
     *
     * Hiding, never receding: a recede would have to fold alpha into RGB
     * (the contour shader writes an opaque colour), so turning it all the
     * way down draws an opaque BLACK contour rather than no contour at
     * all. There is no alpha that removes the structure from the frame.
     *
     * Only correct for a RESULT preview — one that publishes the geometry
     * the operation will leave behind, in the structure's own colour, so
     * the overlay IS the structure as it will be. The live ROI has to go
     * for that to be true: a delete that empties a slice can only read as
     * "this slice will be empty" if nothing is drawn on it.
     *
     * Default false — every existing preview keeps the selected structure
     * on screen as the "before".
     */
    public hidesPreviewedStructure = false;

    public render(view: TextureView): void {
        if (!this._contourTexture) {
            return;
        }
        const contourRenderer = RenderStorage.getRenderer(ContourRenderer);
        contourRenderer.renderContourTexture(
            view,
            this._contourTexture,
            this._color,
            this._contourThickness,
            this.renderOptions
        );
    }

    public get texture(): WebGLTexture | undefined {
        return this._contourTexture?.signedDistanceField.texture;
    }

    public get contourTexture(): ContourTexture | undefined {
        return this._contourTexture;
    }

    /**
     * Recolor without recreating the wrapper: {@link render} reads the
     * color per frame, so the next redraw picks the new value up. Lets a
     * preview follow the user's color choice with no texture rebuild.
     */
    public set color(value: ReadonlyVec3) {
        this._color = value;
    }

    /** The colour this wrapper is drawn in right now. */
    public get color(): ReadonlyVec3 {
        return this._color;
    }

    /**
     * Widen the preview outline. The selected structure draws at 2x its own
     * line width while this overlay defaults to 1.5, so on edges the two
     * share the ROI wins and the RESULT boundary — the line the clinician
     * is being asked to accept — is the one that gets fringed. A preview
     * that traces the selected structure raises this so the diff is the
     * bright line. Read per frame by {@link render}, like the color.
     */
    public set contourThickness(value: number) {
        this._contourThickness = value;
    }

    public set contourTexture(value: ContourTexture | undefined) {
        this._contourTexture = value;
    }

    public get grid(): Grid {
        return this._grid;
    }

    /** Disposes the wrapped ContourTexture's GPU resources. */
    public delete() {
        if (this._contourTexture) {
            this._contourTexture.delete();
        }
    }
}
